import { useState, useEffect, useCallback } from "react"
import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import Stack from "@mui/material/Stack"
import Drawer from "@mui/material/Drawer"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import CircularProgress from "@mui/material/CircularProgress"
import { useBoolean } from "@/src/hooks/use-boolean"
import { Iconify } from "@/src/common/iconify"
import { Scrollbar } from "@/src/common/scrollbar"
import { AddNewUserModal } from "@/src/features/internalUsers/modals/AddNewUserModal"
import { EditUserModal } from "@/src/features/internalUsers/modals/EditUserModal"
import { LinkExistingUserModal } from "@/src/features/internalUsers/modals/LinkExistingUserModal"
import { deleteUser, getWorkspacesUsers, type InternalWorkspaceUserApi } from "@/src/services/internal"
import { useInternalStore } from "@/src/store/slices/internal.slice"
import { useAuthStore } from "@/src/store/slices/auth.slice"
import { getRoleDisplayLabel } from "@/src/features/internalUsers/constants"

/** @deprecated Use InternalWorkspaceUserApi for new code. Kept for backward compatibility. */
export type WorkspaceUser = {
    id: string
    name: string
    email: string
    role: string
    passwordMasked?: string
}

export type UsersSideViewWorkspace = {
    id: string
}

export const USERS_SIDE_VIEW_WIDTH = 420

type UsersSideViewProps = {
    workspace: UsersSideViewWorkspace
    open: boolean
    onClose: () => void
}

export function UsersSideView({ workspace, open, onClose }: UsersSideViewProps) {
    const currentUser = useAuthStore((state) => state.user)
    const setWorkspaceUsers = useInternalStore((state) => state.setWorkspaceUsers)
    const addUserModal = useBoolean()
    const linkExistingModal = useBoolean()
    const [editUser, setEditUser] = useState<InternalWorkspaceUserApi | null>(null)
    const [users, setUsers] = useState<InternalWorkspaceUserApi[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const fetchUsers = useCallback(() => {
        if (!workspace.id) return
        setLoading(true)
        setError(null)
        getWorkspacesUsers(workspace.id)
            .then((res) => {
                const data = res.data ?? []
                setUsers(data)
                setWorkspaceUsers(data)
            })
            .catch(() => setError("Failed to load users"))
            .finally(() => setLoading(false))
    }, [workspace.id, setWorkspaceUsers])

    useEffect(() => {
        if (open && workspace.id) fetchUsers()
    }, [open, workspace.id, fetchUsers])

    const handleDeleteUser = (userId: string) => {
        if (!workspace.id) return
        deleteUser(workspace.id, userId)
            .then(() => {
                fetchUsers()
            })
            .catch(() => setError("Failed to delete user"))
    }

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                variant="persistent"
                sx={{
                    "& .MuiDrawer-paper": {
                        width: USERS_SIDE_VIEW_WIDTH,
                        borderLeft: 1,
                        borderColor: "divider",
                        mt: 0,
                        boxShadow: (theme) => theme.shadows[4],
                    },
                }}
            >
                <Stack sx={{ height: "100%", overflow: "hidden" }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ p: 2, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}
                    >
                        <Typography variant="h6" fontWeight={600}>
                            Users
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Iconify icon="mdi:link-variant" width={18} />}
                                sx={{ minWidth: 0 }}
                                onClick={linkExistingModal.onTrue}
                            >
                                Link Existing
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<Iconify icon="mingcute:add-line" width={18} />}
                                onClick={addUserModal.onTrue}
                            >
                                Add New
                            </Button>
                            <IconButton size="small" onClick={onClose} aria-label="Close sidebar">
                                <Iconify icon="eva:chevron-right-fill" width={22} />
                            </IconButton>
                        </Stack>
                    </Stack>
                    <Scrollbar sx={{ flexGrow: 1, px: 2, py: 1.5 }}>
                        {error && (
                            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
                                {error}
                            </Typography>
                        )}
                        {loading ? (
                            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                                <CircularProgress size={32} />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Loading users...
                                </Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                {users.map((user) => (
                                    <Card
                                        key={user.userId}
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            borderColor: "divider",
                                        }}
                                    >
                                        <Stack spacing={1.5}>
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="flex-start"
                                            >
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight={600}>
                                                        {user.fullName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {user.email}
                                                    </Typography>
                                                </Box>
                                                <Stack direction="row" spacing={0.5}>
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Edit user"
                                                        onClick={() => setEditUser(user)}
                                                    >
                                                        <Iconify icon="solar:pen-bold" width={18} />
                                                    </IconButton>
                                                    {currentUser?.id !== user.userId && (
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            aria-label="Delete user"
                                                            onClick={() => handleDeleteUser(user.userId)}
                                                        >
                                                            <Iconify icon="solar:trash-bin-trash-bold" width={18} />
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                            </Stack>
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: "inline-block",
                                                    alignSelf: "flex-start",
                                                    px: 1.25,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    typography: "caption",
                                                    fontWeight: 600,
                                                    bgcolor: "grey.200",
                                                    color: "text.secondary",
                                                }}
                                            >
                                                {getRoleDisplayLabel(user.role)}
                                            </Box>
                                        </Stack>
                                    </Card>
                                ))}
                            </Stack>
                        )}
                    </Scrollbar>
                </Stack>
            </Drawer>

            <AddNewUserModal
                open={addUserModal.value}
                onClose={addUserModal.onFalse}
                workspaceId={workspace.id}
                onSuccess={() => {
                    addUserModal.onFalse()
                    fetchUsers()
                }}
            />

            <LinkExistingUserModal
                open={linkExistingModal.value}
                onClose={linkExistingModal.onFalse}
                onSuccess={() => {
                    linkExistingModal.onFalse()
                    fetchUsers()
                }}
            />

            <EditUserModal
                open={Boolean(editUser)}
                onClose={() => setEditUser(null)}
                user={editUser}
                workspaceId={workspace.id}
                onSuccess={() => {
                    setEditUser(null)
                    fetchUsers()
                }}
            />
        </>
    )
}
